/*
 * Copyright 2023, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { searchKeywordByScopeSelector, searchKeywordSelector } from "../../../store/search/selector";
import { updateKeyword, resetKeyword, updateKeywordForScope, resetKeywordForScope } from "../../../store/search/reducer";
import SearchIcon from "@mui/icons-material/Search";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";

const SearchBox: React.FC<{ tooltip?: React.ReactNode; scope?: string; placeholder?: string; width?: number | string }> = ({ tooltip, scope, placeholder = "Search...", width }) => {
  const search = useSelector(scope ? searchKeywordByScopeSelector(scope) : searchKeywordSelector);
  const dispatch = useDispatch();
  const [input, setInput] = useState("");
  const [keyword, setKeyword] = useState("");

  const dispatchUpdateKeyword = (value: string) => {
    if (scope) {
      dispatch(updateKeywordForScope({ scope, keyword: value }));
    } else {
      dispatch(updateKeyword(value));
    }
  };

  const dispatchResetKeyword = () => {
    if (scope) {
      dispatch(resetKeywordForScope({ scope }));
    } else {
      dispatch(resetKeyword());
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter" || e.key === "Tab") {
      setKeyword(input);
      if (input === "" || input.length < 3) {
        dispatchResetKeyword();
      } else {
        if (input.length > 2) {
          dispatchUpdateKeyword(keyword);
        }
      }
    }
  };
  const handleKeyUp = (e: any) => {
    if (e.key === "Delete") {
      setKeyword(input);
      if (input === "" || input.length < 3) {
        dispatchResetKeyword();
      }
    }
  };

  const handleFieldBlur = (e: any) => {
    if (input === "" || input.length < 3) {
      dispatchResetKeyword();
    } else if (input.length > 2) {
      setKeyword(input);
      dispatchUpdateKeyword(keyword);
    }
  };

  const handleChange = (e: any) => {
    setInput(e.target.value);
  };
  useEffect(() => {
    setInput(search);
  }, [search]);

  useEffect(() => {
    setKeyword(input);
  }, [input]);
  const searchField = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        background: 'var(--card)',
        border: '1px solid var(--input)',
        borderRadius: 6,
        padding: '0 12px',
        minWidth: width ?? 160,
        maxWidth: width ?? 220,
        height: 32,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      }}
    >
      <SearchIcon style={{ color: 'var(--muted-foreground)', fontSize: 18, marginRight: 4 }} />
      <input
        type="text"
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: 14,
          color: 'var(--foreground)',
          width: '100%',
          height: '100%',
        }}
        id="radix-adornment-search"
        aria-describedby="search-text"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBlur={handleFieldBlur}
        value={input}
        placeholder={placeholder || "Search..."}
      />
    </div>
  );

  if (!tooltip) return searchField;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{searchField}</TooltipTrigger>
        <TooltipContent side="top" align="start" sideOffset={12} className="z-[1400]">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
export default SearchBox;
